import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import {
    Color,
    forLifeOf,
    Icons,
    LifecycleAware,
    ResponsiveService,
    TableAppearance
} from '@lsq-frontend/common-ui';
import {
    ListBusinessUnitSummariesRequest,
    ListResponseBusinessUnitSummaryDto
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/account-summary/list-business-unit-summaries';
import { I18n } from '@ngx-translate/i18n-polyfill';

@LifecycleAware()
@Component({
    selector: 'account-business-units-table',
    template: `
        <ng-template #searchForm>
            <form id="customers-filters-form" [formGroup]="businessUnitSearchFormGroup">
                <lsq-input
                    [placeholder]="searchPlaceholderI18n"
                    formControlName="businessUnitName"
                    [name]="'businessUnitName'"
                    [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                    [group]="businessUnitSearchFormGroup"
                >
                </lsq-input>
            </form>
        </ng-template>
        <card
            *ngIf="!(responsiveService.mdBelow | async)"
            [headerTitle]="businessUnitsCardHeaderI18n"
            id="business-units-table-card"
        >
            <div right-header-content>
                <ng-container *ngTemplateOutlet="searchForm"></ng-container>
            </div>
            <div card-body>
                <lsq-data-table [dataSource]="dataSource" [layoutFixed]="true" [isPaginated]="true">
                    <lsq-column
                        [column]="{
                            header: idHeaderI18n,
                            key: 'buyerId',
                            truncateTextCss: true,
                            width: '20%',
                            sortable: true
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            header: businessUnitNameHeaderI18n,
                            key: 'buyerName',
                            width: '20%',
                            truncateTextCss: true,
                            sortable: true
                        }"
                    >
                    </lsq-column>
                    <lsq-number-column
                        [column]="{
                            header: enrolledSuppliersHeaderI18n,
                            key: 'enrolledSuppliersCount',
                            width: '9rem',
                            sortable: true
                        }"
                    >
                    </lsq-number-column>
                    <lsq-number-column
                        [column]="{
                            header: openInvoicesHeaderI18n,
                            key: 'openInvoiceCount',
                            width: '9rem',
                            sortable: true
                        }"
                    >
                    </lsq-number-column>
                    <lsq-currency-column
                        [column]="{
                            header: openInvoiceTotalHeaderI18n,
                            key: 'openInvoiceTotal',
                            width: '15%',
                            sortable: true
                        }"
                    >
                    </lsq-currency-column>
                    <lsq-currency-column
                        [column]="{
                            header: totalEarlyPaidHeaderI18n,
                            key: 'totalEarlyPaid',
                            width: '15%',
                            sortable: true
                        }"
                    >
                    </lsq-currency-column>
                    <lsq-percent-column
                        [column]="{
                            header: percentUsedHeaderI18n,
                            key: 'percentUsed',
                            width: '10rem',
                            sortable: true
                        }"
                    >
                    </lsq-percent-column>
                    <lsq-currency-column
                        [column]="{
                            header: amountDueNowHeaderI18n,
                            key: 'amountDue',
                            width: '15%',
                            sortable: true
                        }"
                    >
                    </lsq-currency-column>
                    <lsq-currency-column
                        [column]="{
                            header: totalBalanceHeaderI18n,
                            key: 'totalBalance',
                            width: '15%',
                            sortable: true
                        }"
                    >
                    </lsq-currency-column>
                </lsq-data-table>
            </div>
        </card>

        <!-- Mobile -->
        <div *ngIf="responsiveService.mdBelow | async">
            <h1>{{ businessUnitsCardHeaderI18n }}</h1>
            <div>
                <ng-container *ngTemplateOutlet="searchForm"></ng-container>
            </div>
            <lsq-data-table-mobile [dataSource]="dataSource" [isPaginated]="true">
                <ng-template #mobileCardHeader let-row="row">
                    <div class="mtc-header-wrapper">
                        <div class="mtc-header-content">
                            <div class="business-units-mobile-card-header">
                                <div class="mtc-header-content-left">
                                    <div class="mtc-header-title">
                                        {{ row.buyerName }}
                                    </div>
                                    <div class="mtc-header-sub-title">
                                        {{ idHeaderI18n }}:&nbsp;
                                        {{ row.buyerId }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </ng-template>
                <ng-template #mobileCardColumns let-row="row">
                    <div class="card-item">
                        <div class="card-item-label">
                            {{ enrolledSuppliersHeaderI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.enrolledSuppliersCount | number }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            {{ openInvoicesHeaderI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.openInvoiceCount | number }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            {{ openInvoiceTotalHeaderI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.openInvoiceTotal | lsqCurrency }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            {{ totalEarlyPaidHeaderI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.totalEarlyPaid | lsqCurrency }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            {{ percentUsedHeaderI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.percentUsed | lsqPercent }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            {{ amountDueNowHeaderI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.amountDue | lsqCurrency }}
                        </div>
                    </div>
                    <div class="card-item">
                        <div class="card-item-label">
                            {{ totalBalanceHeaderI18n }}
                        </div>
                        <div class="card-item-value">
                            {{ row.totalBalance | lsqCurrency }}
                        </div>
                    </div>
                </ng-template>
            </lsq-data-table-mobile>
        </div>
    `
})
export class AccountBusinessUnitsTableComponent implements OnInit {
    @Input() public dataSource!: LsqApiServiceListSource<
        ListResponseBusinessUnitSummaryDto,
        ListBusinessUnitSummariesRequest
    >;
    public readonly businessUnitSearchFormGroup = new FormGroup({
        businessUnitName: new FormControl()
    });

    public readonly icons = Icons;
    public readonly color = Color;
    public readonly tableAppearance = TableAppearance;

    public readonly businessUnitsCardHeaderI18n = this._i18n({
        value: 'Business Units',
        meaning: `Description of the card contents`,
        description: 'Card header'
    });
    public readonly searchPlaceholderI18n = this._i18n({
        value: 'Search Business Units',
        meaning: `CTA to search for business units`,
        description: 'Placeholder in input search box'
    });
    public readonly idHeaderI18n = this._i18n({
        value: 'Id',
        meaning: `Identifier of the Business Unit`,
        description: 'Table column header'
    });
    public readonly businessUnitNameHeaderI18n = this._i18n({
        value: 'Business Unit',
        meaning: `Name of the Business Unit`,
        description: 'Table column header'
    });
    public readonly enrolledSuppliersHeaderI18n = this._i18n({
        value: 'Enrolled Suppliers',
        meaning: `Number of enrolled suppliers for the Business Unit`,
        description: 'Table column header'
    });
    public readonly openInvoicesHeaderI18n = this._i18n({
        value: 'Open Invoices',
        meaning: `Number of open invoices for suppliers of the Business Unit`,
        description: 'Table column header'
    });
    public readonly openInvoiceTotalHeaderI18n = this._i18n({
        value: 'Open Invoice Total',
        meaning: `Total amount of open invoices for the suppliers of the Business Unit`,
        description: 'Table column header'
    });
    public readonly totalEarlyPaidHeaderI18n = this._i18n({
        value: 'Total Early Paid',
        meaning: `Total amount of early paid invoices for the suppliers of the Business Unit`,
        description: 'Table column header'
    });
    public readonly percentUsedHeaderI18n = this._i18n({
        value: '% Used',
        meaning: `Percent of invoices that have been early paid to suppliers for the Business Unit`,
        description: 'Table column header'
    });
    public readonly amountDueNowHeaderI18n = this._i18n({
        value: 'Amount Due Now',
        meaning: `Amount due now for the Business Unit`,
        description: 'Table column header'
    });
    public readonly totalBalanceHeaderI18n = this._i18n({
        value: 'Total Balance',
        meaning: `Total balance for the Business Unit`,
        description: 'Table column header'
    });

    constructor(public responsiveService: ResponsiveService, private _i18n: I18n) {}

    public ngOnInit(): void {
        this.businessUnitSearchFormGroup
            .get('businessUnitName')
            ?.valueChanges.pipe(forLifeOf(this))
            .subscribe((val) => {
                this.dataSource.dispatchRequest({
                    filter: {
                        ...this.dataSource.request.filter,
                        searchField: val
                    }
                });
            });
    }
}
